/* The Effect Theme - Main JavaScript */
/* theffectonline.com */

'use strict';

// ===================== HERO SLIDESHOW =====================
class HeroSlideshow {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.hero-slideshow__track');
    this.slides = el.querySelectorAll('.hero-slide');
    this.dots = el.querySelectorAll('.hero-slideshow__dot');
    this.prevBtn = el.querySelector('.hero-slideshow__prev');
    this.nextBtn = el.querySelector('.hero-slideshow__next');
    this.current = 0;
    this.total = this.slides.length;
    this.autoplayInterval = null;
    this.autoplayDelay = parseInt(el.dataset.autoplay) || 5000;
    this.isVisible = true;
    if (this.total < 2) return;
    this.init();
  }

  init() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.startAutoplay();
    this.el.addEventListener('mouseenter', () => this.stopAutoplay());
    this.el.addEventListener('mouseleave', () => { if (this.isVisible) this.startAutoplay(); });
    this.initVisibilityObserver();
    let startX = 0;
    this.track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.track.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) diff > 0 ? this.next() : this.prev();
    });
  }

  // Pause the autoplay interval (and its compositing-layer transform animation) while the
  // slideshow is scrolled off-screen — purely a safety/performance measure, resumes normal
  // autoplay the instant the section is visible again.
  initVisibilityObserver() {
    if (!('IntersectionObserver' in window)) return;
    this._io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        this.isVisible = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }, { threshold: 0 });
    this._io.observe(this.el);
  }

  goTo(index) {
    this.current = (index + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.current * 100}%)`;
    this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.current));
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }
  startAutoplay() { this.stopAutoplay(); this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay); }
  stopAutoplay() { clearInterval(this.autoplayInterval); }
}

// ===================== ANNOUNCEMENT BAR =====================
function initAnnouncementBar() {
  const bar = document.querySelector('.announcement-bar');
  if (!bar) return;
  const track = bar.querySelector('.announcement-bar__track');
  if (!track) return;
  track.innerHTML += track.innerHTML;
}

// ===================== OFFSCREEN CONTINUOUS-ANIMATION PAUSE =====================
// The announcement bar marquee and hero decorative circles animate continuously via CSS
// keyframes on GPU-compositing layers. Pausing them (via a CSS class) while their section is
// scrolled out of the viewport is a safety/performance measure only — it does not change how
// they look or behave while on-screen, which is the normal case during a homepage scroll.
function initOffscreenAnimationPause() {
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const isOffscreen = !entry.isIntersecting;
      if (entry.target.classList.contains('announcement-bar')) {
        const track = entry.target.querySelector('.announcement-bar__track');
        track?.classList.toggle('is-offscreen', isOffscreen);
      } else {
        entry.target.classList.toggle('is-offscreen', isOffscreen);
      }
    });
  }, { threshold: 0 });

  document.querySelectorAll('.announcement-bar').forEach(el => io.observe(el));
  document.querySelectorAll('.hero-deco').forEach(el => io.observe(el));
}

// NOTE: legacy initSearch()/initMobileMenu() dead functions removed (2026-09-22) — targeted .header-search-btn/.search-panel/.mobile-menu-btn/.mobile-menu elements that do not exist in current header markup (sections/header.liquid uses its own inline <script> for .te-header__*/.te-mobile-nav__* interactions instead).

// ===================== CART DRAWER =====================
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer-overlay');
    this.closeBtn = document.querySelector('.cart-drawer__close');
    this.openBtns = document.querySelectorAll('.cart-icon-btn');
    this.countEls = document.querySelectorAll('.cart-count');
    this.FREE_THRESHOLD = 600000;
    this.init();
  }

  init() {
    this.openBtns.forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); this.open(); }));
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // Use touchend on mobile for instant response, click for desktop
    const isTouchDevice = 'ontouchstart' in window;
    const eventName = isTouchDevice ? 'touchend' : 'click';

    // Track touch-start position so a light brush or a scroll/swipe gesture that merely starts
    // or passes over the ATC button isn't mistaken for a deliberate tap. Touch events keep the
    // original touchstart element as e.target even if the finger moves away before lifting, so
    // without this check any light touch on the button (e.g. while scrolling the page) was
    // silently adding the product to cart.
    let touchStartX = 0, touchStartY = 0, touchStartTime = 0;
    if (isTouchDevice) {
      document.addEventListener('touchstart', e => {
        const t = e.touches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
        touchStartTime = Date.now();
      }, { passive: true });
    }

    document.addEventListener(eventName, async e => {
      const atcBtn = e.target.closest('.product-card__atc, .btn-atc');
      if (!atcBtn) return;
      if (atcBtn.disabled || atcBtn.classList.contains('is-soldout')) return;

      if (isTouchDevice) {
        const touch = e.changedTouches && e.changedTouches[0];
        if (touch) {
          const dx = Math.abs(touch.clientX - touchStartX);
          const dy = Math.abs(touch.clientY - touchStartY);
          const dt = Date.now() - touchStartTime;
          // Real tap = finger barely moved and was on the button briefly. Anything wider than
          // ~10px of movement or a long press (>600ms, likely a scroll hold) is ignored.
          if (dx > 10 || dy > 10 || dt > 600) return;
        }
      }

      e.preventDefault();
      e.stopPropagation();
      const variantId = atcBtn.dataset.variantId;
      if (!variantId) return;

      // Visual feedback instantly
      const originalText = atcBtn.innerHTML;
      atcBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg> Added!';
      atcBtn.style.background = '#D4537E';
      atcBtn.disabled = true;

      try {
        await this.addItem(variantId, 1);
        showToast('✅ Added to cart!');
        this.open();
      } catch(err) {
        showToast('Something went wrong, please try again', 'error');
      } finally {
        setTimeout(() => {
          atcBtn.innerHTML = originalText;
          atcBtn.style.background = '';
          atcBtn.disabled = false;
        }, 1500);
      }
    });
  }

  open() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.fetchCart();
  }

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();
      this.renderCart(cart);
    } catch(e) { console.error('Cart fetch error:', e); }
  }

  async addItem(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    const data = await res.json();
    this.renderCart(data.items ? data : await (await fetch('/cart.js', { cache: 'no-store' })).json());
    return data;
  }

  async updateItem(key, qty) {
    const row = document.querySelector(`.cart-item[data-key="${key}"]`);
    const controls = row ? row.querySelectorAll('.qty-btn, .cart-item__remove') : [];
    controls.forEach(el => el.disabled = true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await res.json();
      this.renderCart(cart);
    } catch(e) {
      console.error('Cart update error:', e);
      controls.forEach(el => el.disabled = false);
    }
  }

  updateDeliveryBar(totalPrice) {
    const bar  = document.querySelector('.free-delivery-bar');
    const text = document.querySelector('.free-delivery-bar__text');
    const fill = document.querySelector('.free-delivery-bar__fill');
    if (!bar || !text || !fill) return;
    const remaining = this.FREE_THRESHOLD - totalPrice;
    const progress  = Math.min(100, Math.round(totalPrice * 100 / this.FREE_THRESHOLD));
    fill.style.width = progress + '%';
    if (remaining <= 0) {
      bar.classList.add('free-delivery-bar--done');
      text.innerHTML = '&#127881; You\'ve unlocked FREE delivery!';
    } else {
      bar.classList.remove('free-delivery-bar--done');
      const amt = 'Rs.' + (remaining / 100).toLocaleString('en-PK');
      text.innerHTML = 'Add <strong>' + amt + '</strong> more for FREE delivery';
    }
  }

  renderCart(cart) {
    const fmt = (n) => 'Rs. ' + (n / 100).toLocaleString('en-PK');
    const body        = document.querySelector('.cart-drawer__body');
    const totalEl     = document.querySelector('.cart-total .amount');
    const checkoutBtn = document.querySelector('.cart-checkout-btn');
    this.countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });
    if (totalEl)     totalEl.textContent     = fmt(cart.total_price);
    if (checkoutBtn) checkoutBtn.textContent = 'Checkout — ' + fmt(cart.total_price);
    this.updateDeliveryBar(cart.total_price);
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          <p style="font-weight:600;margin-bottom:6px">Your cart is empty</p>
          <p style="font-size:13px">Add some gorgeous products!</p>
        </div>`;
      return;
    }
    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__img">
          <a href="${item.url}"><img src="${item.image}" alt="${item.title}" loading="lazy"></a>
        </div>
        <div class="cart-item__info">
          <div class="cart-item__title"><a href="${item.url}">${item.product_title}</a></div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size:11px;color:#999;margin-bottom:4px">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${fmt(item.final_line_price)}</div>
          <div class="cart-item__qty">
            <button class="qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
            <span class="qty-num">${item.quantity}</span>
            <button class="qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
        </button>
      </div>`).join('');
    body.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty);
        const newQty = btn.dataset.action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        this.updateItem(key, newQty);
      });
    });
    body.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        btn.disabled = true;
        this.updateItem(btn.dataset.key, 0);
      });
    });
  }
}

// ===================== STICKY HEADER =====================
function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('is-scrolled', window.scrollY > 80);
  }, { passive: true });
}

// ===================== WISHLIST (localStorage) =====================
const Wishlist = {
  key: 'te_wishlist',
  get() { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch { return []; } },
  set(ids) { localStorage.setItem(this.key, JSON.stringify(ids)); },
  toggle(id) {
    const ids = this.get();
    const idx = ids.indexOf(id);
    if (idx > -1) { ids.splice(idx, 1); } else { ids.push(id); }
    this.set(ids);
    return idx === -1;
  },
  has(id) { return this.get().includes(id); }
};

function initWishlist() {
  document.querySelectorAll('.product-card__wishlist').forEach(btn => {
    const id = btn.dataset.productId;
    if (!id) return;
    if (Wishlist.has(id)) btn.classList.add('is-active');
    btn.addEventListener('click', () => {
      btn.classList.toggle('is-active', Wishlist.toggle(id));
    });
  });
}

// ===================== NOTIFICATION TOAST =====================
function showToast(msg, type = 'success') {
  let container = document.getElementById('te-toast');
  if (!container) {
    container = document.createElement('div');
    container.id = 'te-toast';
    container.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);z-index:9999;pointer-events:none;width:90%;max-width:300px;';
    document.body.appendChild(container);
  }
  const el = document.createElement('div');
  el.style.cssText = `background:${type === 'success' ? '#D4537E' : '#c0392b'};color:#fff;padding:13px 20px;border-radius:12px;font-size:14px;font-weight:600;box-shadow:0 6px 24px rgba(0,0,0,0.25);text-align:center;opacity:1;transition:opacity 0.4s;`;
  el.textContent = msg;
  container.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 400); }, 2500);
}

// ===================== DISABLE PINCH-ZOOM (root fix for the "page shrinks / white margin" bug) =====================
// Root cause: the viewport meta tag sets user-scalable=no / maximum-scale=1, but modern
// browsers (iOS Safari especially, and some Android Chrome versions) largely IGNORE that meta
// restriction for accessibility reasons and allow pinch-zoom anyway. When a user's fingers land
// close together on the homepage (e.g. while swiping a slider) and pinch inward, the browser
// zooms the whole page OUT — which looks exactly like the "white margin / shrunk page" glitch
// reported on both iPhone Safari and Android Chrome. CSS touch-action alone is not reliably
// enough to stop this across both engines, so we explicitly intercept and cancel the native
// pinch gestures in JS.
function initPinchZoomBlock() {
  // Safari (iOS) fires proprietary gesture events for pinch — cancel them outright.
  document.addEventListener('gesturestart', e => e.preventDefault());
  document.addEventListener('gesturechange', e => e.preventDefault());
  document.addEventListener('gestureend', e => e.preventDefault());

  // Cross-browser (incl. Android Chrome): any touchmove with 2+ fingers is a pinch — cancel it.
  // Must be a non-passive listener since we call preventDefault().
  document.addEventListener('touchmove', e => {
    if (e.touches && e.touches.length > 1) e.preventDefault();
  }, { passive: false });

  // Double-tap-to-zoom guard (Safari/Chrome both support this as an alternate zoom trigger).
  let lastTouchEnd = 0;
  document.addEventListener('touchend', e => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) e.preventDefault();
    lastTouchEnd = now;
  }, { passive: false });
}

// ===================== INIT =====================
document.addEventListener('DOMContentLoaded', () => {
  initPinchZoomBlock();
  initAnnouncementBar();
  initStickyHeader();
  initWishlist();
  document.querySelectorAll('.hero-slideshow').forEach(el => new HeroSlideshow(el));
  initOffscreenAnimationPause();
  window.TheEffectCart = new CartDrawer();
  window.showToast = showToast;
});
